/**
 * Collection image tabs: series filter + optional banner under facets-summary.
 */
(function () {
  'use strict';

  const FILTER_PARAM = 'filter.p.m.custom.product_series';
  const BANNER_SELECTOR = '[data-collection-img-tab-banner]';

  /**
   * Image-tab nav that syncs product_series facets and the series banner.
   */
  class CollectionImgTab extends HTMLElement {
    connectedCallback() {
      if (this._bound) {
        return;
      }
      this._bound = true;
      this._onClick = this._handleClick.bind(this);
      this._onFacetUpdate = this._handleFacetUpdate.bind(this);
      this._onMoreClick = this._handleMoreClick.bind(this);
      this._onPrevClick = this._handlePrevClick.bind(this);
      this._onListScroll = this._syncMoreButton.bind(this);
      this._onMqChange = this._scheduleCompactFromScroll.bind(this);
      this._onWindowScroll = this._scheduleCompactFromScroll.bind(this);
      this._items = Array.from(this.querySelectorAll('[data-filter-value]'));
      this._list = this.querySelector('.collection-img-tab__list');
      this._moreButton = this.querySelector('.collection-img-tab__more--next');
      this._prevButton = this.querySelector('.collection-img-tab__more--prev');
      this._stickyEl = this.closest('.collection-img-tab-sticky');
      this._sectionEl = this.closest('.shopify-section');
      this._isCompact = false;
      this._compactLocked = false;
      this._mq = window.matchMedia('(max-width: 999px)');
      this._stickyMq = window.matchMedia('(max-width: 999px)');
      this._tabData = this._readTabData();
      this.addEventListener('click', this._onClick);
      document.addEventListener('facet:update', this._onFacetUpdate);
      window.addEventListener('scroll', this._onWindowScroll, { passive: true });
      if (this._moreButton) {
        this._moreButton.addEventListener('click', this._onMoreClick);
      }
      if (this._prevButton) {
        this._prevButton.addEventListener('click', this._onPrevClick);
      }
      if (this._list) {
        this._list.addEventListener('scroll', this._onListScroll, { passive: true });
      }
      this._resizeObserver =
        typeof ResizeObserver === 'undefined'
          ? null
          : new ResizeObserver(() => {
              this._syncMoreButton();
            });
      if (this._resizeObserver && this._list) {
        this._resizeObserver.observe(this._list);
      }
      this._ensureFlowSentinel();
      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._onMqChange);
        this._stickyMq.addEventListener('change', this._onMqChange);
      } else if (this._mq.addListener) {
        this._mq.addListener(this._onMqChange);
        this._stickyMq.addListener(this._onMqChange);
      }
      this._observeMainSection();
      this._ensureStickyScope();
      this.syncFromUrl();
      this.renderBanner();
      this._syncMoreButton();
      this._syncCompactFromScroll();
    }

    disconnectedCallback() {
      this._bound = false;
      this.removeEventListener('click', this._onClick);
      document.removeEventListener('facet:update', this._onFacetUpdate);
      if (this._moreButton) {
        this._moreButton.removeEventListener('click', this._onMoreClick);
      }
      if (this._prevButton) {
        this._prevButton.removeEventListener('click', this._onPrevClick);
      }
      if (this._list) {
        this._list.removeEventListener('scroll', this._onListScroll);
      }
      window.removeEventListener('scroll', this._onWindowScroll);
      if (this._compactRaf) {
        window.cancelAnimationFrame(this._compactRaf);
        this._compactRaf = 0;
      }
      if (this._flowSentinel && this._flowSentinel.parentNode) {
        this._flowSentinel.remove();
      }
      this._flowSentinel = null;
      if (this._headerProbe && this._headerProbe.parentNode) {
        this._headerProbe.remove();
      }
      this._headerProbe = null;
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
      }
      if (this._observer) {
        this._observer.disconnect();
      }
      this._releaseStickyScope();
      if (this._mq) {
        if (this._mq.removeEventListener) {
          this._mq.removeEventListener('change', this._onMqChange);
        } else if (this._mq.removeListener) {
          this._mq.removeListener(this._onMqChange);
        }
      }
      if (this._stickyMq) {
        if (this._stickyMq.removeEventListener) {
          this._stickyMq.removeEventListener('change', this._onMqChange);
        } else if (this._stickyMq.removeListener) {
          this._stickyMq.removeListener(this._onMqChange);
        }
      }
    }

    /**
     * @returns {Array<{value: string, bannerTitle: string, bannerText: string, bannerImage: string|null}>}
     */
    _readTabData() {
      const script = this.querySelector('[data-collection-img-tab-data]');
      if (!script) {
        return [];
      }
      try {
        const parsed = JSON.parse(script.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    }

    /**
     * Limit sticky to the img-tab + main-collection block.
     * Native sticky sticks until the containing block ends; without this wrap
     * the containing block is #main, so the bar stays pinned through FAQ/blog.
     */
    _ensureStickyScope() {
      const tabSection = this._sectionEl;
      if (!tabSection) {
        return;
      }
      const existing = tabSection.closest('.collection-sticky-scope');
      if (existing) {
        this._stickyScope = existing;
        return;
      }
      const mainSection = tabSection.nextElementSibling;
      if (!mainSection || !mainSection.classList.contains('shopify-section--main-collection')) {
        return;
      }
      const parent = tabSection.parentNode;
      if (!parent) {
        return;
      }
      const scope = document.createElement('div');
      scope.className = 'collection-sticky-scope';
      const sentinel = this._flowSentinel;
      const insertBeforeNode =
        sentinel && sentinel.parentNode === parent ? sentinel : tabSection;
      parent.insertBefore(scope, insertBeforeNode);
      if (sentinel && sentinel.parentNode === parent) {
        scope.appendChild(sentinel);
      }
      scope.appendChild(tabSection);
      scope.appendChild(mainSection);
      this._stickyScope = scope;
    }

    /**
     * Put the two sections back so theme editor / navigation does not leak a wrapper.
     */
    _releaseStickyScope() {
      const scope = this._stickyScope;
      this._stickyScope = null;
      if (!scope || !scope.parentNode) {
        return;
      }
      const parent = scope.parentNode;
      while (scope.firstChild) {
        parent.insertBefore(scope.firstChild, scope);
      }
      scope.remove();
    }

    /**
     * Re-paint the banner after main-collection AJAX morph.
     */
    _observeMainSection() {
      const mainSection = document.querySelector('.shopify-section--main-collection');
      if (!mainSection || typeof MutationObserver === 'undefined') {
        return;
      }
      this._observer = new MutationObserver(() => {
        this.renderBanner();
      });
      this._observer.observe(mainSection, { childList: true });
    }

    /**
     * @param {MouseEvent} event
     */
    _handleClick(event) {
      const item = event.target.closest('[data-filter-value]');
      if (!item || !this.contains(item)) {
        return;
      }
      event.preventDefault();
      const value = item.getAttribute('data-filter-value') || '';
      this._setActive(value);
      this.renderBanner();
      this._dispatchFacetUpdate(value);
    }

    /**
     * Scroll the tab row when a fade chevron is tapped.
     * @param {number} direction
     */
    _scrollTabs(direction) {
      if (!this._list) {
        return;
      }
      const distance = Math.max(this._list.clientWidth * 0.6, 120);
      this._list.scrollBy({ left: direction * distance, behavior: 'smooth' });
    }

    /**
     * Scroll the tab row when the trailing chevron is tapped.
     * @param {MouseEvent} event
     */
    _handleMoreClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this._scrollTabs(1);
    }

    /**
     * Scroll the tab row when the leading chevron is tapped.
     * @param {MouseEvent} event
     */
    _handlePrevClick(event) {
      event.preventDefault();
      event.stopPropagation();
      this._scrollTabs(-1);
    }

    /**
     * Toggle a fade chevron's visibility.
     * @param {HTMLButtonElement|null} button
     * @param {boolean} isVisible
     */
    _setChevronVisible(button, isVisible) {
      if (!button) {
        return;
      }
      button.hidden = !isVisible;
      button.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
      button.tabIndex = isVisible ? 0 : -1;
    }

    /**
     * Show fade chevrons while the row can still scroll in that direction.
     */
    _syncMoreButton() {
      if (!this._list) {
        return;
      }
      const canScroll = this._list.scrollWidth - this._list.clientWidth > 8;
      const atStart = this._list.scrollLeft <= 8;
      const atEnd = this._list.scrollLeft + this._list.clientWidth >= this._list.scrollWidth - 8;
      this._setChevronVisible(this._prevButton, canScroll && !atStart);
      this._setChevronVisible(this._moreButton, canScroll && !atEnd);
      if (this._list) {
        this._list.classList.toggle('has-prev', canScroll && !atStart);
        this._list.classList.toggle('has-next', canScroll && !atEnd);
      }
    }

    /**
     * Resolved pixel height of the sticky header area.
     * --sticky-area-height is a calc() on :root, so parseFloat() is NaN.
     * @returns {number}
     */
    _stickyHeaderHeight() {
      if (this._headerProbe && this._headerProbe.isConnected) {
        return this._headerProbe.getBoundingClientRect().height;
      }
      const probe = document.createElement('div');
      probe.setAttribute('aria-hidden', 'true');
      probe.style.cssText =
        'position:absolute;visibility:hidden;pointer-events:none;height:var(--sticky-area-height, 0px)';
      document.body.appendChild(probe);
      this._headerProbe = probe;
      return probe.getBoundingClientRect().height;
    }

    /**
     * In-flow marker placed before the sticky Shopify section.
     * Compact is based on this node so hiding images cannot flip the stuck check.
     */
    _ensureFlowSentinel() {
      const section = this._sectionEl || this.closest('.shopify-section');
      if (!section || !section.parentNode) {
        return;
      }
      if (this._flowSentinel && this._flowSentinel.isConnected) {
        return;
      }
      const sentinel = document.createElement('div');
      sentinel.className = 'collection-img-tab-flow-sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      section.parentNode.insertBefore(sentinel, section);
      this._flowSentinel = sentinel;
    }

    /**
     * Coalesce scroll/media updates onto one animation frame.
     */
    _scheduleCompactFromScroll() {
      if (this._compactRaf) {
        return;
      }
      this._compactRaf = window.requestAnimationFrame(() => {
        this._compactRaf = 0;
        this._syncCompactFromScroll();
      });
    }

    /**
     * Compact after the flow sentinel has traveled under the header.
     * Hysteresis avoids enter/leave oscillation at the sticky edge.
     * Do not read the sticky section's own box: hiding images shortens it and
     * would fight scroll position. overflow-anchor: none on the section stops
     * the browser from rewriting scrollY when compact height changes.
     */
    _syncCompactFromScroll() {
      if (!this._mq || !this._mq.matches) {
        this._compactLocked = false;
        this._setCompact(false);
        return;
      }
      this._ensureFlowSentinel();
      const sentinel = this._flowSentinel;
      if (!sentinel) {
        return;
      }
      const headerHeight = this._stickyHeaderHeight();
      const underHeader = headerHeight - sentinel.getBoundingClientRect().top;
      const enterAt = 24;
      const leaveAt = 8;
      let compact;
      if (this._compactLocked) {
        compact = underHeader > leaveAt;
        if (!compact) {
          this._compactLocked = false;
        }
      } else {
        compact = underHeader > enterAt;
        if (compact) {
          this._compactLocked = true;
        }
      }
      this._setCompact(compact);
    }

    /**
     * @param {boolean} compact
     */
    _setCompact(compact) {
      if (this._isCompact === compact) {
        return;
      }
      this._isCompact = compact;
      this.classList.toggle('is-compact', compact);
      if (this._stickyEl) {
        this._stickyEl.classList.toggle('is-compact', compact);
      }
      if (this._sectionEl) {
        this._sectionEl.classList.toggle('is-compact', compact);
      }
      this._syncMoreButton();
    }

    /**
     * Swap Reset / chips without remounting Filter trigger (avoids a wrap flash).
     * @param {Element} current
     * @param {Element} next
     */
    _patchFilterChrome(current, next) {
      const currentBar = current.querySelector('.collection-filter-bar');
      const nextBar = next.querySelector('.collection-filter-bar');
      if (currentBar && nextBar) {
        const currentReset = currentBar.querySelector('.collection-filter-bar__reset');
        const nextReset = nextBar.querySelector('.collection-filter-bar__reset');
        if (nextReset && currentReset) {
          currentReset.replaceWith(document.importNode(nextReset, true));
        } else if (nextReset && !currentReset) {
          currentBar.appendChild(document.importNode(nextReset, true));
        } else if (!nextReset && currentReset) {
          currentReset.remove();
        }
      }

      const currentChips = current.querySelector('.active-facets--chips');
      const nextChips = next.querySelector('.active-facets--chips');
      if (nextChips && currentChips) {
        currentChips.replaceWith(document.importNode(nextChips, true));
      } else if (nextChips && !currentChips) {
        current.appendChild(document.importNode(nextChips, true));
      } else if (!nextChips && currentChips) {
        currentChips.remove();
      }
    }

    /**
     * Re-render Filter / Reset / chips after collection AJAX so they stay in the sticky unit.
     * @param {URL} [url]
     */
    async _refreshFilterChrome(url) {
      const section = this._sectionEl || this.closest('.shopify-section');
      const current = section && section.querySelector('.collection-img-tab-filter');
      if (!section || !current) {
        return;
      }
      const sectionId = section.id.replace(/^shopify-section-/, '');
      const requestUrl = new URL(url || window.location.href, window.location.origin);
      requestUrl.searchParams.set('section_id', sectionId);
      try {
        const html = await fetch(requestUrl.toString()).then((response) => response.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const next = doc.querySelector('.collection-img-tab-filter');
        if (next) {
          this._patchFilterChrome(current, next);
        }
      } catch (error) {}
    }

    _handleFacetUpdate(event) {
      const nextUrl = event && event.detail && event.detail.url;
      window.requestAnimationFrame(() => {
        this.syncFromUrl();
        this.renderBanner();
        this._refreshFilterChrome(nextUrl);
        this._syncCompactFromScroll();
      });
    }

    /**
     * Match the active tab to the current product_series URL param.
     */
    syncFromUrl() {
      const url = new URL(window.location.href);
      const selected = url.searchParams.get(FILTER_PARAM);
      if (selected) {
        this._setActive(selected);
        return;
      }
      const defaultItem = this._items.find((item) => item.hasAttribute('data-default')) || this._items[0];
      if (defaultItem) {
        this._setActive(defaultItem.getAttribute('data-filter-value') || '');
      }
    }

    /**
     * @param {string} value
     */
    _setActive(value) {
      this._items.forEach((item) => {
        const isActive = item.getAttribute('data-filter-value') === value;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-current', isActive ? 'page' : 'false');
      });
      this._activeValue = value;
    }

    /**
     * Fill or hide the banner mount under facets-summary.
     */
    renderBanner() {
      const mount = document.querySelector(BANNER_SELECTOR);
      if (!mount) {
        return;
      }
      const tab = this._tabData.find((entry) => entry.value === this._activeValue);
      const title = tab && tab.bannerTitle ? String(tab.bannerTitle).trim() : '';
      const text = tab && tab.bannerText ? String(tab.bannerText).trim() : '';
      const image = tab && tab.bannerImage ? String(tab.bannerImage).trim() : '';
      const hasBanner = Boolean(title || text || image);

      if (!hasBanner) {
        mount.hidden = true;
        mount.replaceChildren();
        mount.style.backgroundImage = '';
        return;
      }

      mount.hidden = false;
      mount.style.backgroundImage = image ? 'url("' + image.replace(/"/g, '\\"') + '")' : '';

      const nodes = [];
      if (title) {
        const heading = document.createElement('p');
        heading.className = 'collection-img-tab-banner__title';
        heading.textContent = title;
        nodes.push(heading);
      }
      if (text) {
        const description = document.createElement('p');
        description.className = 'collection-img-tab-banner__text';
        description.textContent = text;
        nodes.push(description);
      }
      mount.replaceChildren(...nodes);
    }

    /**
     * @param {string} filterValue
     */
    _dispatchFacetUpdate(filterValue) {
      const form = document.querySelector('#facet-form');
      if (!form) {
        return;
      }
      const url = this._buildFacetUrl(form, filterValue);
      form.dispatchEvent(
        new CustomEvent('facet:update', {
          bubbles: true,
          detail: { url: url }
        })
      );
    }

    /**
     * @param {HTMLFormElement} form
     * @param {string} filterValue
     * @returns {URL}
     */
    _buildFacetUrl(form, filterValue) {
      const searchParams = new URLSearchParams(new FormData(form));
      const url = new URL(form.action);
      url.search = '';
      searchParams.delete(FILTER_PARAM);
      searchParams.delete('page');
      searchParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });
      if (filterValue && filterValue !== 'All') {
        url.searchParams.set(FILTER_PARAM, filterValue);
      }
      ['page', 'filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
        if (url.searchParams.get(key) === '') {
          url.searchParams.delete(key);
        }
      });
      const sectionId = form.getAttribute('section-id');
      if (sectionId) {
        url.searchParams.set('section_id', sectionId);
      }
      return url;
    }
  }

  if (!window.customElements.get('collection-img-tab')) {
    window.customElements.define('collection-img-tab', CollectionImgTab);
  }
})();
